import React from "react";
import PropTypes from "prop-types";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {BsPersonFill} from "react-icons/bs";
import WS from "../../../ws";
import {getGroupMessages, sendGroupMessage, sendGroupMedia, markGroupRead, pinGroupMessage} from "../../../Services/campaignFunctions";
import {groupLabel} from "../GroupListItem";
import {avatarColorFor, groupPhotoUrl} from "../../helpers/avatar";
import GroupAvatar from "../GroupAvatar";
import {memberLimit} from "../../helpers/memberLimit";
import {useGroupManager} from "../../Providers/groupManager";
import GroupMessageBubble from "../GroupMessageBubble";
import GroupComposeBar from "../GroupComposeBar";
import {
    Container, Header, HeaderAvatar, HeaderInfo, HeaderName, HeaderMembers, Body, State,
} from "./styles";

// base (fetch, já ordenado) + os itens de extra (WS chegado durante o fetch) que não estão por id.
const mergeById = (base, extra) => {
    const ids = new Set(base.map((m) => m.id));
    return [...base, ...extra.filter((m) => !ids.has(m.id))];
};

/**
 * Conversa do grupo selecionado: busca as mensagens, assina o WS pra tempo real,
 * e renderiza header + balões. Sem grupo -> estado "selecione um grupo".
 * É montado com key={group.id} pelo Inbox, então o estado reseta por grupo.
 */
const GroupConversation = ({group, onPinned}) => {
    const {t} = useTranslation();
    const {campaign, membersCountByGroup, can} = useGroupManager();
    const {id: campaignId} = useParams();
    const [messages, setMessages] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(false);
    const bodyRef = React.useRef(null);
    const nearBottomRef = React.useRef(true);

    // fetch inicial
    React.useEffect(() => {
        if (!group) return;
        let alive = true;
        setLoading(true);
        setError(false);
        (async () => {
            const res = await getGroupMessages(campaignId, group.id);
            if (!alive) return;
            if (res?.isAxiosError || res instanceof Error) {
                setError(true);
                setMessages([]);
            } else {
                const ordered = [...(Array.isArray(res) ? res : [])].reverse(); // desc -> asc
                setMessages((prev) => mergeById(ordered, prev)); // não perde WS chegado durante o fetch
            }
            setLoading(false);
        })();
        return () => {
            alive = false;
        };
    }, [group, campaignId]);

    // tempo real
    React.useEffect(() => {
        if (!group) return undefined;
        return WS.subscribe((msg) => {
            if (msg?.subject === "group_message" && msg.data?.group_id === group.id) {
                setMessages((prev) => (prev.some((m) => m.id === msg.data.id) ? prev : [...prev, msg.data]));
            }
            if (msg?.subject === "group_reaction" && msg.data?.group_id === group.id) {
                setMessages((prev) => prev.map((m) => (m.id === msg.data.message_id ? {...m, reactions: msg.data.reactions} : m)));
            }
        });
    }, [group]);

    // marca lido no backend ao abrir e ao fechar (montado com key={group.id} pelo Inbox,
    // então mount/unmount dispara por grupo). Best-effort: não trata a resposta.
    React.useEffect(() => {
        if (!group) return undefined;
        markGroupRead(campaignId, group.id);
        return () => {
            markGroupRead(campaignId, group.id);
        };
    }, [group, campaignId]);

    // auto-scroll: ao ABRIR o grupo (primeiro load) começa SEMPRE na última mensagem,
    // com reforço via rAF (fontes/layout assentarem). Depois, só acompanha se o usuário
    // já estava perto do fim. Remonta por grupo (key={group.id}) -> reseta a cada abertura.
    const didInitialScrollRef = React.useRef(false);
    React.useLayoutEffect(() => {
        const el = bodyRef.current;
        if (!el || messages.length === 0) return;
        const toBottom = () => { el.scrollTop = el.scrollHeight; };
        if (!didInitialScrollRef.current) {
            didInitialScrollRef.current = true;
            nearBottomRef.current = true;
            toBottom();
            requestAnimationFrame(toBottom);
        } else if (nearBottomRef.current) {
            toBottom();
        }
    }, [messages]);

    const onScroll = () => {
        const el = bodyRef.current;
        if (el) nearBottomRef.current = el.scrollHeight - el.scrollTop - el.offsetHeight < 80;
    };

    // Envia; a mensagem aparece pelo eco WS (sem append manual).
    const handleSend = (content) => sendGroupMessage(campaignId, group.id, content);
    const handleSendMedia = (file, caption) => sendGroupMedia(campaignId, group.id, file, caption);

    // fixa a msg e avisa o Inbox pra o painel rebuscar as fixadas
    const handlePin = async (messageId) => {
        const res = await pinGroupMessage(campaignId, group.id, messageId);
        if (!(res?.isAxiosError || res instanceof Error)) onPinned?.();
    };

    if (!group) {
        return (
            <Container>
                <State>{t("words.select_a_group")}</State>
            </Container>
        );
    }

    const name = groupLabel(group);
    return (
        <Container>
            <Header>
                <HeaderAvatar $color={avatarColorFor(group.id)}><GroupAvatar name={name} photoUrl={groupPhotoUrl(campaign)}/></HeaderAvatar>
                <HeaderInfo>
                    <HeaderName>{name}</HeaderName>
                    <HeaderMembers>
                        <BsPersonFill/> {membersCountByGroup?.[group.id] ?? group.members_count} / {memberLimit(campaign)} {t("words.members")}
                    </HeaderMembers>
                </HeaderInfo>
            </Header>
            <Body ref={bodyRef} onScroll={onScroll}>
                {loading && <State>{t("words.loading")}</State>}
                {!loading && error && <State>{t("words.something_wrong")}</State>}
                {!loading && !error && messages.length === 0 && <State>{t("words.no_messages")}</State>}
                {!loading && !error && messages.map((m) => (
                    <GroupMessageBubble key={m.id} message={m} onPin={handlePin}/>
                ))}
            </Body>
            <GroupComposeBar onSend={handleSend} onSendMedia={handleSendMedia} canSend={can("can_send_messages")}/>
        </Container>
    );
};

GroupConversation.propTypes = {
    group: PropTypes.object,
    onPinned: PropTypes.func,
};

export default GroupConversation;
